import { css } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';

import { FocusRing } from '@ingka/common-styles-webc';

import TextToggleLayoutEnum from '../../enums/TextToggleLayoutEnum.js';

/**
 * A class with style.
 */
export default class TextToggleStyle {
	/**
	 * Returns styles.
	 *
	 * @param options Args.
	 * @param options.expanded Is it open.
	 * @param options.layout Text layout.
	 * @param options.theme Theme.
	 * @param options.printLabel Label displayed when printing.
	 * @returns Styles.
	 */
	public static getStyle(options: {
		theme: ITheme;
		layout: string;
		expanded: boolean;
		printLabel: string | null;
	}): string {
		const { spacing, color, motion } = options.theme;
		return css`
			${FocusRing.CSS}

			button {
				width: 100%;
				display: inline-flex;
				align-items: flex-start;
				color: ${options.layout === TextToggleLayoutEnum.emphasized
					? color.accent.primaryAccent
					: color.neutral.grey900};
				border: none;
				background: none;
				cursor: pointer;
				margin: 0;
				padding: 0;
			}
			button:hover {
				text-decoration: underline;
			}
			button kompis-icon {
				margin-top: ${6 / 16}rem;
			}
			.divider {
				display: flex;
				flex-shrink: 0;
				width: ${spacing.space75 / 16}rem;
			}
			kompis-icon {
				transform: rotate(${options.expanded ? 180 : 0}deg);
				transition-property: transform;
				transition-duration: ${motion.duration.medium};
			}

			.visuallyHidden {
				border: 0 !important;
				clip: rect(${1 / 16}rem, ${1 / 16}rem, ${1 / 16}rem, ${1 / 16}rem) !important;
				-webkit-clip-path: inset(50%) !important;
				clip-path: inset(50%) !important;
				height: ${1 / 16}rem !important;
				margin: -${1 / 16}rem !important;
				overflow: hidden !important;
				padding: 0 !important;
				position: absolute !important;
				width: ${1 / 16}rem !important;
				white-space: nowrap !important;
				top: 0;
				bottom: 0;
				left: 0;
				right: 0;
			}

			${this.getRtlRules(options)}
			${this.getPrintStyle(options)}
		`;
	}

	/**
	 * Returns styles.
	 *
	 * @param options The props.
	 * @param options.theme Theme.
	 * @param options.expanded ...
	 * @returns Styles.
	 */
	private static getRtlRules(options: { theme: ITheme; expanded: boolean }): string {
		if (!options.expanded) {
			return css``;
		}
		return css`
			:host(:dir(rtl)) kompis-icon {
				transform: rotate(-180deg);
			}
			:host-context([dir='rtl']) kompis-icon {
				transform: rotate(-180deg);
			}
		`;
	}

	/**
	 * Returns styles.
	 *
	 * @param options Options.
	 * @param options.printLabel Label displayed when printing.
	 * @returns Styles.
	 */
	private static getPrintStyle(options: { printLabel: string | null }): string {
		return css`
			@media print {
				kompis-icon,
				.divider {
					display: none;
				}
				${options.printLabel
					? css`
							kompis-text span {
								display: none;
							}
							kompis-text:after {
								content: '${options.printLabel}';
							}
						`
					: ''}
			}
		`;
	}
}
